import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';

/**
 * Help copy for the rich PowerPoint template editor.
 * @param {object} props - Props.
 * @param {boolean} props.open - Dialog visibility.
 * @param {function(): void} props.onClose - Close handler.
 * @returns {React.ReactElement}
 */
function PowerPointHelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableRestoreFocus>
      <DialogTitle>How PowerPoint templates work</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" component="h3" gutterBottom>
          Slides and variables
        </Typography>
        <Typography variant="body2" paragraph>
          Each slide stores HTML with optional{' '}
          <code>{'{{variable_name}}'}</code> placeholders. When you generate a presentation, Inky fills each
          variable from your project requirements.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          AI prompts
        </Typography>
        <Typography variant="body2" paragraph>
          Under each slide, every variable has an AI prompt: the instruction Inky follows when it writes that slot.
          Imports suggest one for you. Edit it to steer the wording, length or focus. A variable used on several
          slides shares one prompt, and renaming a variable keeps its prompt. Prompts are saved with the template.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Import from .pptx
        </Typography>
        <Typography variant="body2" paragraph>
          Upload a reference deck to extract text structure and theme colors. Edit the result before saving as a
          template. Extraction is best-effort, so complex layouts may need manual cleanup.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Formatting
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body2">
            <strong>Formatting:</strong> Bold, italic, underline, colors, highlights
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Structure:</strong> Headers, bullet points, numbered lists, tables
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Variables:</strong> Use <code>{'{{variable_name}}'}</code> for AI-filled content
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom sx={{ mt: 2 }}>
          Preview
        </Typography>
        <Typography variant="body2" paragraph>
          Use the Preview button in the header to see all slides with variables highlighted (read-only). Switch back
          to Edit to change content.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Tips
        </Typography>
        <Typography variant="body2" paragraph>
          • Use descriptive variable names like <code>{'{{project_goals}}'}</code>
          <br />
          • Keep bullets short: slides are generated for on-screen reading
          <br />• Slide IDs are kept stable when you save so imports stay traceable
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PowerPointHelpDialog;
